// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: pick when to be reminded about a message. Quick picks + custom time
// + an optional note; the Assistant DMs a card with a jump link at that time.
// Without a message (the phone widget's "Remind me") it is a bare note reminder
// and the note is required.

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {CANCEL_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import KonvoReminders from '@app/features/konvo_reminders/state/KonvoReminders';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {Button} from '@app/features/ui/button/Button';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {DateTimePickerField} from '@app/features/ui/components/form/DateTimePickerField';
import {Input} from '@app/features/ui/components/form/FormInput';
import {BellRingingIcon} from '@app/features/ui/icons/Icons';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {type CSSProperties, useCallback, useMemo, useState} from 'react';

const TITLE_DESCRIPTOR = msg({message: 'Remind me about this', comment: 'Title of the remind-me modal.'});
const NOTE_DESCRIPTOR = msg({message: 'Note (optional)', comment: 'Field label in the remind-me modal.'});
const NOTE_PLACEHOLDER_DESCRIPTOR = msg({
	message: 'e.g. Follow up with Markus',
	comment: 'Placeholder for the note field in the remind-me modal.',
});
const CUSTOM_DESCRIPTOR = msg({
	message: 'Custom time',
	comment: 'Label for the custom date picker in the remind-me modal.',
});
const CONFIRM_DESCRIPTOR = msg({message: 'Set reminder', comment: 'Confirm button in the remind-me modal.'});
const SET_DESCRIPTOR = msg({
	message: 'Reminder set. The Assistant will DM you.',
	comment: 'Toast after setting a reminder.',
});
const FAILED_DESCRIPTOR = msg({
	message: 'Could not set the reminder. Try again.',
	comment: 'Toast when setting a reminder fails.',
});

interface QuickPick {
	key: string;
	label: string;
	compute: () => Date;
}

function at(date: Date, hour: number, minute = 0): Date {
	const next = new Date(date);
	next.setHours(hour, minute, 0, 0);
	return next;
}

function nextWeekday(from: Date, weekday: number): Date {
	const next = new Date(from);
	const delta = (weekday - next.getDay() + 7) % 7 || 7;
	next.setDate(next.getDate() + delta);
	return next;
}

function buildQuickPicks(now: Date): Array<QuickPick> {
	const inMinutes = (min: number) => new Date(now.getTime() + min * 60_000);
	const tomorrow = new Date(now);
	tomorrow.setDate(now.getDate() + 1);
	const picks: Array<QuickPick> = [
		{key: '20m', label: 'In 20 minutes', compute: () => inMinutes(20)},
		{key: '1h', label: 'In 1 hour', compute: () => inMinutes(60)},
		{key: '3h', label: 'In 3 hours', compute: () => inMinutes(180)},
		{key: 'tomorrow', label: 'Tomorrow 09:00', compute: () => at(tomorrow, 9)},
		{key: 'monday', label: 'Monday 09:00', compute: () => at(nextWeekday(now, 1), 9)},
	];
	if (now.getHours() < 16) {
		picks.splice(3, 0, {key: 'today', label: 'Today 17:00', compute: () => at(now, 17)});
	}
	return picks;
}

const pickStyle = (active: boolean): CSSProperties => ({
	padding: '10px 12px',
	borderRadius: 10,
	border: `1px solid ${active ? 'var(--brand-primary, #5865f2)' : 'var(--background-modifier-accent, rgba(255,255,255,0.08))'}`,
	background: active
		? 'color-mix(in srgb, var(--brand-primary, #5865f2) 22%, transparent)'
		: 'var(--background-secondary, rgba(255,255,255,0.04))',
	color: 'var(--text-primary, #f2f3f5)',
	font: 'inherit',
	fontSize: 13,
	fontWeight: 600,
	cursor: 'pointer',
	textAlign: 'left',
});

interface KonvoRemindMeModalProps {
	/** Leave out for a bare note reminder. */
	message?: Message;
	onClose: () => void;
	/** Time and note proposed by the assistant; the user can still change both. */
	initial?: {remindAt: Date; note?: string};
	onCreated?: () => void;
}

export const KonvoRemindMeModal = ({message, onClose, initial, onCreated}: KonvoRemindMeModalProps) => {
	const {i18n} = useLingui();
	const picks = useMemo(() => buildQuickPicks(new Date()), []);
	const [selected, setSelected] = useState<string>(initial ? 'custom' : (picks[0]?.key ?? ''));
	const [custom, setCustom] = useState<Date | null>(initial?.remindAt ?? null);
	const [note, setNote] = useState(initial?.note ?? '');
	const [submitting, setSubmitting] = useState(false);
	const remindAt = selected === 'custom' ? custom : (picks.find((pick) => pick.key === selected)?.compute() ?? null);
	const handleConfirm = useCallback(async () => {
		if (!remindAt) return;
		setSubmitting(true);
		try {
			await KonvoReminders.create({
				channelId: message?.channelId,
				messageId: message?.id,
				remindAt,
				note: note.trim() || undefined,
			});
			ToastCommands.createToast({type: 'success', children: i18n._(SET_DESCRIPTOR)});
			onCreated?.();
			onClose();
		} catch {
			ToastCommands.createToast({type: 'error', children: i18n._(FAILED_DESCRIPTOR)});
			setSubmitting(false);
		}
	}, [remindAt, message, note, i18n, onClose, onCreated]);
	const snippet = (message?.content ?? '').replace(/\s+/g, ' ').trim().slice(0, 140);
	return (
		<Modal.Root size="medium" centered onClose={onClose} data-flx="konvo-reminders.remind-me-modal.root">
			<Modal.Header
				icon={<BellRingingIcon size={20} weight="fill" />}
				title={i18n._(TITLE_DESCRIPTOR)}
				data-flx="konvo-reminders.remind-me-modal.header"
			/>
			<Modal.Content data-flx="konvo-reminders.remind-me-modal.content">
				<Modal.ContentLayout data-flx="konvo-reminders.remind-me-modal.layout">
					{snippet && (
						<blockquote
							style={{
								margin: '0 0 12px',
								padding: '8px 12px',
								borderLeft: '3px solid var(--brand-primary, #5865f2)',
								background: 'var(--background-secondary, rgba(255,255,255,0.04))',
								borderRadius: 8,
								color: 'var(--text-secondary, #b5bac1)',
								fontSize: 13,
							}}
						>
							{snippet}
						</blockquote>
					)}
					<div style={{display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, marginBottom: 12}}>
						{picks.map((pick) => (
							<button
								key={pick.key}
								type="button"
								style={pickStyle(selected === pick.key)}
								onClick={() => setSelected(pick.key)}
								data-flx="konvo-reminders.remind-me-modal.quick-pick"
							>
								{pick.label}
								<div style={{fontSize: 11, fontWeight: 500, opacity: 0.7, marginTop: 2}}>
									{pick.compute().toLocaleString(undefined, {weekday: 'short', hour: '2-digit', minute: '2-digit'})}
								</div>
							</button>
						))}
						<button
							type="button"
							style={pickStyle(selected === 'custom')}
							onClick={() => setSelected('custom')}
							data-flx="konvo-reminders.remind-me-modal.custom"
						>
							{i18n._(CUSTOM_DESCRIPTOR)}
						</button>
					</div>
					{selected === 'custom' && (
						<div style={{marginBottom: 12}}>
							<DateTimePickerField value={custom} onChange={setCustom} minDate={new Date()} />
						</div>
					)}
					<Input
						id="konvo-reminder-note"
						type="text"
						label={i18n._(NOTE_DESCRIPTOR)}
						placeholder={i18n._(NOTE_PLACEHOLDER_DESCRIPTOR)}
						value={note}
						onChange={(event) => setNote(event.target.value)}
						autoFocus={!message}
						data-flx="konvo-reminders.remind-me-modal.note"
					/>
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer data-flx="konvo-reminders.remind-me-modal.footer">
				<Button variant="secondary" onClick={onClose} data-flx="konvo-reminders.remind-me-modal.cancel">
					{i18n._(CANCEL_DESCRIPTOR)}
				</Button>
				<Button
					variant="primary"
					onClick={() => void handleConfirm()}
					submitting={submitting}
					disabled={!remindAt || remindAt.getTime() < Date.now() || (!message && !note.trim())}
					data-flx="konvo-reminders.remind-me-modal.confirm"
				>
					{i18n._(CONFIRM_DESCRIPTOR)}
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
};
